import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getGmailStatus, startGmailAuth } from "@/lib/api";

const CHANNEL = "tj-gmail-oauth";
const STORAGE_KEY = "tj_gmail_oauth";
const POLL_MS = 2000;
const TIMEOUT_MS = 180000;

// Gmail OAuth connection state shared by the Lead + Facility panels.
// The callback popup notifies us via BroadcastChannel, localStorage and
// window.opener (Google's COOP often severs the opener, so we use all three).
export function useGmailConnection(reloadKey) {
  const [gmail, setGmail] = useState({ connected: false });
  const [connecting, setConnecting] = useState(false);
  const watch = useRef({ interval: null, timeout: null });
  const lastNonce = useRef(null);

  const stopWatch = () => {
    clearInterval(watch.current.interval);
    clearTimeout(watch.current.timeout);
    watch.current = { interval: null, timeout: null };
  };

  const refresh = useCallback(async () => {
    try {
      const s = (await getGmailStatus()) || { connected: false };
      setGmail(s);
      return s;
    } catch {
      setGmail({ connected: false });
      return { connected: false };
    }
  }, []);

  const handleResult = useCallback(async (data) => {
    if (!data || data.type !== "gmail_oauth_result") return;
    if (data.nonce && data.nonce === lastNonce.current) return;
    lastNonce.current = data.nonce;
    stopWatch();
    const s = await refresh();
    setConnecting(false);
    if (data.ok && s.connected) toast.success(`Gmail connected · ${s.email || data.email || "ready"}`);
    else if (!data.ok) toast.error(data.error || "Gmail connect failed. Please try again.");
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh, reloadKey]);

  useEffect(() => {
    const onMessage = (e) => handleResult(e.data);
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try { handleResult(JSON.parse(e.newValue)); } catch { /* ignore */ }
    };
    const onFocus = () => refresh();
    let bc = null;
    if (typeof BroadcastChannel !== "undefined") {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = (e) => handleResult(e.data);
    }
    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      bc?.close();
      stopWatch();
    };
  }, [handleResult, refresh]);

  const connect = async () => {
    stopWatch();
    setConnecting(true);
    let url;
    try {
      ({ authorize_url: url } = await startGmailAuth());
    } catch (e) {
      setConnecting(false);
      toast.error(e?.response?.data?.detail || "Could not start Gmail connect flow.");
      return;
    }
    const popup = window.open(url, "gmail_connect", "width=520,height=720");
    if (!popup) {
      setConnecting(false);
      toast.error("Popup blocked — allow popups for this site and try again.");
      return;
    }
    watch.current.interval = setInterval(async () => {
      const s = await refresh();
      if (s.connected) {
        stopWatch();
        setConnecting(false);
        return;
      }
      // Popup gone (closed by user, or opener severed) → back to "Connect Gmail".
      // A later success message from the callback still flips us to connected.
      if (popup.closed) {
        stopWatch();
        setConnecting(false);
      }
    }, POLL_MS);
    watch.current.timeout = setTimeout(() => { stopWatch(); setConnecting(false); }, TIMEOUT_MS);
  };

  return { gmail, connecting, connect, refresh };
}
